import { useMemo, useState, useEffect, type MouseEvent } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { ValueType } from 'recharts/types/component/DefaultTooltipContent';

import styles from './styles.module.css';
import {
  generateColors,
  formatXAxis,
  formatTooltip,
  formatTickDate,
  formatTickTime,
  formatYAxisTick,
  generateSingleColor,
  AGGREGATE_LINE_KEY,
  AGGREGATE_LINE_LABEL,
  AGGREGATE_LINE_COLOR,
  AGGREGATE_LINE_WIDTH,
  AGGREGATE_LINE_SHADOW_WIDTH
} from './utils';
import ContainerLoader from '../AdminToolComponents/containerLoader';
import EmptyData from '../Form/EmptyData';
import Labels from '../Form/Labels';
import RangeSlider from '@/components/RangeSlider';

const DEFAULT_CHART_RIGHT_MARGIN = 40;

interface DataPoint {
  timestamp: string;
  [key: string]: string | number;
}

interface PowerChartProps {
  data?: DataPoint[];
  animateSocketValue?: string;
  isLoading?: boolean;
  indexColor?: number;
  unit?: string;
  hideZeroLines?: boolean;
  rightMargin?: number;
  showAggregateLine?: boolean;
  showTurbineLines?: boolean;
  aggregateLineLabel?: string;
}

const CustomLegend = ({
  payload,
  hiddenLines,
  onToggleLine
}: {
  payload?: {
    value: string;
    color: string;
    label?: string;
    isAggregate?: boolean;
  }[];
  hiddenLines: Set<string>;
  onToggleLine: (dataKey: string) => void;
}) => {
  if (!payload) return null;

  const extractTurbineId = (text: string) => {
    return text.split('.')[0];
  };

  const handleClick = (dataKey: string, event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onToggleLine(dataKey);
  };

  return (
    <div className={styles.legendWrapper}>
      <div className={styles.legendContainer}>
        {payload.map((entry, index: number) => {
          const isHidden = hiddenLines.has(entry.value);
          const displayLabel = entry.label ?? extractTurbineId(entry.value);
          return (
            <div
              key={`item-${index}`}
              className={`${styles.legendItem} ${isHidden ? styles.legendItemHidden : ''} ${entry.isAggregate ? styles.legendItemAggregate : ''}`}
              onClick={e => handleClick(entry.value, e)}
              role="button"
              tabIndex={0}
              onKeyPress={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleClick(entry.value, e as any);
                }
              }}
            >
              <div
                className={`${styles.legendColor} ${entry.isAggregate ? styles.legendColorAggregate : ''}`}
                style={{
                  backgroundColor: entry.color,
                  opacity: isHidden ? 0.3 : 1,
                  transition: 'opacity 0.2s ease'
                }}
              />
              <span
                className={styles.legendText}
                style={{
                  opacity: isHidden ? 0.5 : 1,
                  transition: 'opacity 0.2s ease'
                }}
              >
                {displayLabel}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CustomXAxisTick = ({ x, y, payload }: any) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        className={styles.xAxisTick}
      >
        {formatTickDate(payload.value)}
      </text>
      <text
        x={0}
        y={16}
        dy={16}
        textAnchor="middle"
        className={styles.xAxisTick}
      >
        {formatTickTime(payload.value)}
      </text>
    </g>
  );
};

export default function PowerChart({
  data = [],
  animateSocketValue = '',
  isLoading = false,
  indexColor = undefined,
  unit = '',
  hideZeroLines = false,
  rightMargin = DEFAULT_CHART_RIGHT_MARGIN,
  showAggregateLine = false,
  showTurbineLines = true,
  aggregateLineLabel = AGGREGATE_LINE_LABEL
}: PowerChartProps) {
  const [rangeValue, setRangeValue] = useState<[number, number]>([
    0,
    data.length - 1
  ]);
  const [hiddenLines, setHiddenLines] = useState<Set<string>>(new Set());

  useEffect(() => {
    setHiddenLines(new Set());
  }, [showAggregateLine, showTurbineLines]);

  useMemo(() => {
    if (data.length > 0) {
      setRangeValue([0, data.length - 1]);
    }
  }, [data.length]);

  const dataKeys = useMemo(() => {
    if (!data.length) return [];
    return Object.keys(data[0]).filter(
      key => key !== 'timestamp' && key !== AGGREGATE_LINE_KEY
    );
  }, [data]);

  const hasAggregateLine =
    showAggregateLine && data.some(row => AGGREGATE_LINE_KEY in row);

  const zeroKeys = useMemo(() => {
    if (!hideZeroLines || !data.length) return new Set<string>();
    return new Set(
      dataKeys.filter(key => data.every(row => Number(row[key]) === 0))
    );
  }, [data, dataKeys, hideZeroLines]);

  const modeHiddenLines = useMemo(() => {
    const forced = new Set<string>();
    if (!showTurbineLines) {
      dataKeys.forEach(key => forced.add(key));
    }
    return forced;
  }, [dataKeys, showTurbineLines]);

  const effectiveHiddenLines = useMemo(() => {
    const combined = new Set<string>();
    dataKeys.forEach(key => {
      if (modeHiddenLines.has(key)) {
        combined.add(key);
        return;
      }
      const defaultHidden = zeroKeys.has(key);
      if (defaultHidden !== hiddenLines.has(key)) {
        combined.add(key);
      }
    });
    if (hasAggregateLine && hiddenLines.has(AGGREGATE_LINE_KEY)) {
      combined.add(AGGREGATE_LINE_KEY);
    }
    return combined;
  }, [dataKeys, zeroKeys, hasAggregateLine, modeHiddenLines, hiddenLines]);

  const filteredData = useMemo(() => {
    return data.slice(rangeValue[0], rangeValue[1] + 1);
  }, [data, rangeValue]);

  const lineColors = useMemo(() => {
    return indexColor
      ? [generateSingleColor(indexColor)]
      : generateColors(dataKeys.length);
  }, [dataKeys.length, indexColor]);

  const tickInterval = useMemo(() => {
    const dataLength = filteredData.length;
    if (dataLength <= 6) return 0;

    return Math.ceil(dataLength / 6) - 1;
  }, [filteredData.length]);

  const yAxisDomain = useMemo((): [number, number] => {
    const activeKeys = dataKeys.filter(key => !effectiveHiddenLines.has(key));
    if (hasAggregateLine && !effectiveHiddenLines.has(AGGREGATE_LINE_KEY)) {
      activeKeys.push(AGGREGATE_LINE_KEY);
    }
    if (activeKeys.length === 0) {
      return [0, 1];
    }
    let min = Infinity;
    let max = -Infinity;
    for (const row of filteredData) {
      for (const key of activeKeys) {
        const raw = row[key];
        const value = typeof raw === 'number' ? raw : Number(raw);
        if (Number.isFinite(value)) {
          if (value < min) min = value;
          if (value > max) max = value;
        }
      }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return [0, 1];
    }
    if (min === max) {
      const pad = Math.abs(min) * 0.1 || 1;
      return [min - pad, max + pad];
    }
    const padding = (max - min) * 0.05;
    return [min - padding, max + padding];
  }, [filteredData, dataKeys, effectiveHiddenLines, hasAggregateLine]);

  const handleToggleLine = (dataKey: string) => {
    if (modeHiddenLines.has(dataKey)) return;

    setHiddenLines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dataKey)) {
        newSet.delete(dataKey);
      } else {
        newSet.add(dataKey);
      }
      return newSet;
    });
  };

  const legendPayload = useMemo(() => {
    const items: {
      value: string;
      color: string;
      label?: string;
      isAggregate?: boolean;
    }[] = [];

    if (showAggregateLine && hasAggregateLine) {
      items.push({
        value: AGGREGATE_LINE_KEY,
        color: AGGREGATE_LINE_COLOR,
        label: aggregateLineLabel,
        isAggregate: true
      });
    }

    if (showTurbineLines) {
      dataKeys.forEach((key, index) => {
        items.push({ value: key, color: lineColors[index] });
      });
    }

    return items;
  }, [
    dataKeys,
    lineColors,
    hasAggregateLine,
    aggregateLineLabel,
    showAggregateLine,
    showTurbineLines
  ]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <ContainerLoader />;
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className={styles.container}>
        <EmptyData label="No data available" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.chartContainer}>
        {unit && (
          <div className={styles.unit}>
            <Labels color="dark" size="medium" bold>
              {unit}
            </Labels>
          </div>
        )}
        <div className={styles.chartWrapper}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={filteredData}
              margin={{
                top: 20,
                right: rightMargin,
                left: 10,
                bottom: 0
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--color-light-blue-grid)"
              />
              <XAxis
                label={{
                  value: 'Date/Time',
                  position: 'insideBottom'
                }}
                dataKey="timestamp"
                height={80}
                tick={<CustomXAxisTick />}
                tickFormatter={formatTickDate}
                interval={tickInterval}
                stroke="var(--color-light-blue-grid)"
              />
              <YAxis
                scale="linear"
                domain={yAxisDomain}
                tickFormatter={formatYAxisTick}
                stroke="var(--color-light-blue-grid)"
              />
              <Tooltip
                formatter={(value: ValueType) => formatTooltip(value, unit)}
                contentStyle={{
                  stroke: 'var(--color-light-blue-grid)',
                  borderRadius: '4px',
                  padding: '10px'
                }}
                labelFormatter={label => `Time: ${formatXAxis(label)}`}
              />
              {dataKeys.map((key, index) => (
                <Line
                  key={key}
                  type="linear"
                  dataKey={key}
                  stroke={lineColors[index]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  name={key}
                  isAnimationActive={key === animateSocketValue}
                  hide={effectiveHiddenLines.has(key)}
                />
              ))}
              {hasAggregateLine && (
                <>
                  <Line
                    key={`${AGGREGATE_LINE_KEY}-shadow`}
                    type="linear"
                    dataKey={AGGREGATE_LINE_KEY}
                    stroke={AGGREGATE_LINE_COLOR}
                    strokeWidth={AGGREGATE_LINE_SHADOW_WIDTH}
                    strokeOpacity={0.25}
                    dot={false}
                    isAnimationActive={false}
                    hide={effectiveHiddenLines.has(AGGREGATE_LINE_KEY)}
                    tooltipType="none"
                  />
                  <Line
                    key={AGGREGATE_LINE_KEY}
                    type="linear"
                    dataKey={AGGREGATE_LINE_KEY}
                    stroke={AGGREGATE_LINE_COLOR}
                    strokeWidth={AGGREGATE_LINE_WIDTH}
                    dot={false}
                    activeDot={{ r: 5 }}
                    name={aggregateLineLabel}
                    isAnimationActive={false}
                    hide={effectiveHiddenLines.has(AGGREGATE_LINE_KEY)}
                  />
                </>
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {data.length > 0 && (
          <div className={styles.sliderContainer}>
            <RangeSlider
              min={0}
              max={data.length - 1}
              value={rangeValue}
              onChange={setRangeValue}
            />
          </div>
        )}

        <CustomLegend
          payload={legendPayload}
          hiddenLines={effectiveHiddenLines}
          onToggleLine={handleToggleLine}
        />
      </div>
    </div>
  );
}
